import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload as UploadIcon, Download, ArrowLeft, ArrowRight, Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import Papa from 'papaparse';
import { FullPageLoader, LoadingSpinner } from '../components/LoadingSpinner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Upload() {
  const [trip, setTrip] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    role: 'passenger',
    passenger_first_name: '',
    passenger_last_name: '',
    driver_first_name: '',
    driver_last_name: '',
    phone: '',
    address: '',
    capacity: 0,
    notes: '',
    car_license_type: ''
  });
  const fileInputRef = useRef(null);
  const { tripId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  // OPTIMIZATION: Fetch trip and participants in parallel
  useEffect(() => {
    fetchData();
  }, [tripId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Parallel API calls for faster loading
      const [tripResponse, participantsResponse] = await Promise.all([
        axios.get(`${API_URL}/trips/${tripId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/trips/${tripId}/participants`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTrip(tripResponse.data);
      setParticipants(participantsResponse.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/trips/${tripId}/participants/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(response.data.message);
      
      // Show any warnings about skipped rows
      if (response.data.errors && response.data.errors.length > 0) {
        const errorMsg = response.data.errors.slice(0, 3).join('\n');
        toast.warning(`Uploaded with ${response.data.errors.length} warnings:\n${errorMsg}`);
      }
      
      // Refetch only participants, not trip
      const participantsResponse = await axios.get(`${API_URL}/trips/${tripId}/participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipants(participantsResponse.data);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload participants';
      
      // Show detailed error
      if (error.response?.status === 400) {
        // Validation error - show in alert
        alert(`CSV Upload Failed:\n\n${detail}`);
      }
      
      toast.error('CSV upload failed - check error log for details', { duration: 5000 });
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      ['role', 'passenger_first_name', 'passenger_last_name', 'driver_first_name', 'driver_last_name', 'phone', 'address', 'capacity', 'notes', 'car/license type'],
      ['driver', '', '', 'John', 'Smith', '+61412345678', '123 Main St, Melbourne VIC 3000', '3', 'Has a van', 'Full License'],
      ['passenger', 'Jane', 'Doe', '', '', '+61423456789', '456 Queen St, Melbourne VIC 3000', '', 'Vegetarian', 'Learners Permit']
    ];
    
    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nomad-sample.csv';
    a.click();
  };

  const openEditDialog = (participant) => {
    setEditingParticipant({ ...participant });
    setShowEditDialog(true);
  };

  const closeEditDialog = () => {
    setEditingParticipant(null);
    setShowEditDialog(false);
  };

  const saveParticipant = async () => {
    try {
      const updateData = {
        role: editingParticipant.role,
        passenger_first_name: editingParticipant.passenger_first_name || null,
        passenger_last_name: editingParticipant.passenger_last_name || null,
        driver_first_name: editingParticipant.driver_first_name || null,
        driver_last_name: editingParticipant.driver_last_name || null,
        phone: editingParticipant.phone,
        address: editingParticipant.address,
        capacity: editingParticipant.capacity || null,
        notes: editingParticipant.notes || null,
        car_license_type: editingParticipant.car_license_type || null
      };

      await axios.put(
        `${API_URL}/participants/${editingParticipant.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setParticipants(participants.map(p => 
        p.id === editingParticipant.id ? editingParticipant : p
      ));

      toast.success('Participant updated successfully');
      closeEditDialog();
    } catch (error) {
      toast.error('Failed to update participant');
      console.error('Update error:', error);
    }
  };

  const deleteParticipant = async (participantId) => {
    if (!window.confirm('Delete this participant? This cannot be undone.')) return;

    try {
      await axios.delete(`${API_URL}/participants/${participantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setParticipants(participants.filter(p => p.id !== participantId));
      toast.success('Participant deleted');
    } catch (error) {
      toast.error('Failed to delete participant');
      console.error('Delete error:', error);
    }
  };

  const addParticipant = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/trips/${tripId}/participants/manual`,
        newParticipant,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setParticipants([...participants, response.data]);
      toast.success('Participant added successfully');
      setShowAddDialog(false);
      setNewParticipant({
        role: 'passenger',
        passenger_first_name: '',
        passenger_last_name: '',
        driver_first_name: '',
        driver_last_name: '',
        phone: '',
        address: '',
        capacity: 0,
        notes: '',
        car_license_type: ''
      });
    } catch (error) {
      toast.error('Failed to add participant');
      console.error('Add error:', error);
    }
  };

  // OPTIMIZATION: Memoize expensive calculations
  const { driverCount, passengerCount, totalSeats } = useMemo(() => {
    const drivers = participants.filter(p => p.role === 'driver');
    const passengers = participants.filter(p => p.role === 'passenger');
    const seats = drivers.reduce((sum, d) => sum + (d.capacity || 0), 0);
    
    return {
      driverCount: drivers.length,
      passengerCount: passengers.length,
      totalSeats: seats
    };
  }, [participants]);

  if (loading) {
    return <FullPageLoader text="Loading participants..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                {trip?.name || 'Trip'}
              </h1>
              <p className="text-sm text-slate-600 mt-1">Step 1: Upload Participant Data</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button
                onClick={() => navigate(`/trip/${tripId}/map`)}
                disabled={participants.length === 0}
                data-testid="next-to-map"
              >
                Next: Map & Review
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Upload Participants CSV</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={downloadSampleCSV}
                data-testid="download-sample-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Sample CSV
              </Button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-csv-button"
              >
                {uploading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <UploadIcon className="w-4 h-4 mr-2" />
                    Upload CSV
                  </>
                )}
              </Button>

              <Button
                onClick={() => setShowAddDialog(true)}
                data-testid="add-participant-button-top"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Participant
              </Button>
            </div>

            {/* Stats */}
            {participants.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-900">{driverCount}</p>
                    <p className="text-sm text-blue-700">Drivers</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-900">{passengerCount}</p>
                    <p className="text-sm text-blue-700">Passengers</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-900">{totalSeats}</p>
                    <p className="text-sm text-blue-700">Total Seats</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Participants Table */}
        {participants.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Participants ({participants.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="participants-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4">Role</th>
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-left py-3 px-4">Phone</th>
                      <th className="text-left py-3 px-4">Address</th>
                      <th className="text-left py-3 px-4">Capacity</th>
                      <th className="text-left py-3 px-4">Car/License Type</th>
                      <th className="text-left py-3 px-4">Notes</th>
                      <th className="text-left py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => {
                      const name = p.role === 'driver' 
                        ? `${p.driver_first_name || ''} ${p.driver_last_name || ''}`.trim()
                        : `${p.passenger_first_name || ''} ${p.passenger_last_name || ''}`.trim();
                      
                      return (
                        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              p.role === 'driver' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {p.role}
                            </span>
                          </td>
                          <td className="py-3 px-4">{name || 'N/A'}</td>
                          <td className="py-3 px-4">{p.phone}</td>
                          <td className="py-3 px-4">{p.address}</td>
                          <td className="py-3 px-4">
                            {p.role === 'driver' ? p.capacity || 0 : '-'}
                          </td>
                          <td className="py-3 px-4 text-xs text-slate-600">
                            {p.car_license_type || '-'}
                          </td>
                          <td className="py-3 px-4 text-xs text-slate-600">
                            {p.notes || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(p)}
                                title="Edit participant"
                              >
                                <Edit2 className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteParticipant(p.id)}
                                title="Delete participant"
                                data-testid="delete-participant-button"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <Button
                  onClick={() => setShowAddDialog(true)}
                  data-testid="add-participant-button-bottom"
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Participant
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Edit Participant Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Participant</DialogTitle>
          </DialogHeader>
          {editingParticipant && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={editingParticipant.role}
                  onValueChange={(value) => setEditingParticipant({...editingParticipant, role: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="passenger">Passenger</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editingParticipant.role === 'driver' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="driver-first-name">First Name</Label>
                    <Input
                      id="driver-first-name"
                      value={editingParticipant.driver_first_name || ''}
                      onChange={(e) => setEditingParticipant({...editingParticipant, driver_first_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="driver-last-name">Last Name</Label>
                    <Input
                      id="driver-last-name"
                      value={editingParticipant.driver_last_name || ''}
                      onChange={(e) => setEditingParticipant({...editingParticipant, driver_last_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="passenger-first-name">First Name</Label>
                    <Input
                      id="passenger-first-name"
                      value={editingParticipant.passenger_first_name || ''}
                      onChange={(e) => setEditingParticipant({...editingParticipant, passenger_first_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="passenger-last-name">Last Name</Label>
                    <Input
                      id="passenger-last-name"
                      value={editingParticipant.passenger_last_name || ''}
                      onChange={(e) => setEditingParticipant({...editingParticipant, passenger_last_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editingParticipant.phone || ''}
                  onChange={(e) => setEditingParticipant({...editingParticipant, phone: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editingParticipant.address || ''}
                  onChange={(e) => setEditingParticipant({...editingParticipant, address: e.target.value})}
                  className="mt-1"
                />
              </div>

              {editingParticipant.role === 'driver' && (
                <div>
                  <Label htmlFor="edit-capacity">Capacity (Seats)</Label>
                  <Input
                    id="edit-capacity"
                    type="number"
                    value={editingParticipant.capacity || ''}
                    onChange={(e) => setEditingParticipant({...editingParticipant, capacity: parseInt(e.target.value) || 0})}
                    className="mt-1"
                    min="0"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="edit-car-license">Car/License Type</Label>
                <Input
                  id="edit-car-license"
                  value={editingParticipant.car_license_type || ''}
                  onChange={(e) => setEditingParticipant({...editingParticipant, car_license_type: e.target.value})}
                  className="mt-1"
                  placeholder="e.g., Full License, Learners Permit"
                />
              </div>

              <div>
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editingParticipant.notes || ''}
                  onChange={(e) => setEditingParticipant({...editingParticipant, notes: e.target.value})}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button onClick={saveParticipant} className="flex-1">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
                <Button variant="outline" onClick={closeEditDialog} className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Participant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="add-role">Role</Label>
              <Select
                value={newParticipant.role}
                onValueChange={(value) => setNewParticipant({...newParticipant, role: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver">Driver</SelectItem>
                  <SelectItem value="passenger">Passenger</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newParticipant.role === 'driver' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-driver-first-name">First Name</Label>
                  <Input
                    id="new-driver-first-name"
                    value={newParticipant.driver_first_name || ''}
                    onChange={(e) => setNewParticipant({...newParticipant, driver_first_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="new-driver-last-name">Last Name</Label>
                  <Input
                    id="new-driver-last-name"
                    value={newParticipant.driver_last_name || ''}
                    onChange={(e) => setNewParticipant({...newParticipant, driver_last_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-passenger-first-name">First Name</Label>
                  <Input
                    id="new-passenger-first-name"
                    value={newParticipant.passenger_first_name || ''}
                    onChange={(e) => setNewParticipant({...newParticipant, passenger_first_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="new-passenger-last-name">Last Name</Label>
                  <Input
                    id="new-passenger-last-name"
                    value={newParticipant.passenger_last_name || ''}
                    onChange={(e) => setNewParticipant({...newParticipant, passenger_last_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="new-phone">Phone *</Label>
              <Input
                id="new-phone"
                value={newParticipant.phone || ''}
                onChange={(e) => setNewParticipant({...newParticipant, phone: e.target.value})}
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="new-address">Address *</Label>
              <Input
                id="new-address"
                value={newParticipant.address || ''}
                onChange={(e) => setNewParticipant({...newParticipant, address: e.target.value})}
                className="mt-1"
                required
              />
            </div>

            {newParticipant.role === 'driver' && (
              <div>
                <Label htmlFor="new-capacity">Capacity (Seats)</Label>
                <Input
                  id="new-capacity"
                  type="number"
                  value={newParticipant.capacity || ''}
                  onChange={(e) => setNewParticipant({...newParticipant, capacity: parseInt(e.target.value) || 0})}
                  className="mt-1"
                  min="0"
                />
              </div>
            )}

            <div>
              <Label htmlFor="new-car-license">Car/License Type</Label>
              <Input
                id="new-car-license"
                value={newParticipant.car_license_type || ''}
                onChange={(e) => setNewParticipant({...newParticipant, car_license_type: e.target.value})}
                className="mt-1"
                placeholder="e.g., Full License, Learners Permit"
              />
            </div>

            <div>
              <Label htmlFor="new-notes">Notes</Label>
              <Textarea
                id="new-notes"
                value={newParticipant.notes || ''}
                onChange={(e) => setNewParticipant({...newParticipant, notes: e.target.value})}
                rows={3}
                className="mt-1"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={addParticipant} className="flex-1" data-testid="confirm-add-participant">
                <Plus className="w-4 h-4 mr-2" />
                Add Participant
              </Button>
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="fixed bottom-0 w-full py-4 text-center bg-white/80 backdrop-blur border-t border-slate-200">
        <a
          href="https://donate.stripe.com/5kQ6oH99f72ze6OcIK4ZG00"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Support this project
        </a>
      </footer>
    </div>
  );
}
